import { Sponsorstripe } from '@schibsted/advertory/aftenposten/cjs';
import React, { useEffect, useState } from 'react';

import type { ArticleProps } from '../types.js';
import type { SaveListItem } from '../../../../types/save-types.js';

import Text from '../articleComponents/Text/index.js';
import Authors from '../articleComponents/Authors.js';
import SoMe from '../articleComponents/SoMe.js';
import Grades from '../articleComponents/Grades.js';
import About from '..//articleComponents/About/index.js';
import Tags from '../articleComponents/Tags.js';
import Image from '../articleComponents/Image.js';
import { Save } from '../../saving/Save.js';
import Navigation from '../../commonComponents/Navigation/Navigation.js';
import Footer from '../../../components/commonComponents/Footer.js';
import {
  isMovieReview,
  isRestaurantReview,
  restSaveTags,
  sectionMapping,
  topSaveTags,
} from '../helpers.js';
import { Recommended } from '../articleComponents/Recommended.js';
import {
  LinkWithPulse,
  buildImpressionUIElementAttributes,
} from '../../../../pulse/index.js';
import { Newsletter } from '../../newsletter/Newsletter.js';
import { useListsContext } from '../../saving/ListsProvider.js';
import { useDelayedVisibility } from '../../../hooks/useDelayedVisibility.js';
import Toast from '../../commonComponents/Toast.js';
import ComponentSelector from '../articleComponents/ComponentSelector.js';
import MediaFactBox from '../articleComponents/MediaFactBox.js';
import { useRuterAdsMessaging } from '../../../hooks/useRuterAdsMessaging.js';

export const ReviewArticle: React.FC<ArticleProps> = ({
  article,
  recommendations,
  godtTeasers,
  isMobile,
  isHermesApp,
  forwardedRef,
  enableRuterCampaign,
}) => {
  const {
    components,
    authors,
    title,
    lead,
    factBox,
    grades,
    published,
    updated,
  } = article;

  const [overlay, toggleOverlay] = useState(false);
  const { statusMessage, hasError } = useListsContext();
  const [showToast] = useDelayedVisibility(statusMessage, 1500, 1500);

  const isRestaurantReviewArticle = isRestaurantReview(article);

  const handleOverlay = () => {
    toggleOverlay(!overlay);
  };

  useRuterAdsMessaging({ article, disable: !isRestaurantReviewArticle });

  useEffect(() => {
    if (window.Iterate && isRestaurantReviewArticle) {
      window.Iterate('install', '6566f3a394b4920001c4ce17');
    }
  }, [article, isRestaurantReviewArticle]);

  const reviewTeaser: SaveListItem = {
    id: article.id,
    title: article?.restaurantTitle || title,
    imageId: article?.mainImage?.id,
    slug: article.slug,
    grade: article.grade,
    kicker: topSaveTags(article) || '',
    tags: restSaveTags(article),
  };

  return (
    <div
      className={`review-article ${isMobile ? 'mobile' : 'desktop'}-article`}
    >
      {overlay && <Save handleOverlay={handleOverlay} item={reviewTeaser} />}
      {showToast && hasError && <Toast message={statusMessage} hasError />}
      <Navigation
        fullWidth={!isMobile}
        activeSection={sectionMapping[article.section]}
        isTransparent
        isHermesApp={isHermesApp}
      />
      <Sponsorstripe targetId="sponsorstripe" />
      <div className="front-image-wrapper">
        {article?.mainImage && (
          <Image
            imageAsset={article.mainImage}
            hideByline
            fullWidth
            isMobile={isMobile}
            articleTitle={title}
          />
        )}
      </div>
      <div ref={forwardedRef}>
        <div className="article-container">
          <section
            data-pulse-entity-id={
              isRestaurantReviewArticle
                ? 'about-container:restaurant'
                : 'about-container:movie'
            }
            className="about-container"
            {...buildImpressionUIElementAttributes({
              object: {
                objectId: isRestaurantReviewArticle
                  ? 'about-container:restaurant'
                  : 'about-container:movie',
                name: 'About container',
                elementType: 'section',
              },
              placement: {
                containerName: 'page',
                foldPosition: 'Unknown',
              },
            })}
          >
            <About
              article={article}
              handleOverlay={handleOverlay}
              item={reviewTeaser}
              isRestaurantReview={isRestaurantReviewArticle}
              isMobile={isMobile}
              enableRuterCampaign={enableRuterCampaign}
            />
            {isRestaurantReviewArticle && grades && grades.length > 0 && (
              <Grades grades={grades} />
            )}
            {!isRestaurantReviewArticle && factBox && (
              <MediaFactBox paragraphs={factBox} type="fact" />
            )}
          </section>

          {title && <h1 className="title">{title}</h1>}

          {lead && <Text {...lead} />}

          <Authors
            authors={authors}
            published={published}
            updated={updated}
            section={sectionMapping[article.section]}
          />
          <Newsletter isMovieReview={isMovieReview(article)} />
          <div className="article-content">
            {components
              .filter((c) => c !== lead)
              .filter((c) => isRestaurantReviewArticle || c.type !== 'fact')
              .map((c, index) => (
                <ComponentSelector
                  item={c}
                  index={index}
                  articleTitle={title}
                  isMobile={!!isMobile}
                  groupIndex={0}
                  isGuide={false}
                  key={index}
                />
              ))}

            <Tags tags={article.tags} type={'hererdet'} />
          </div>
          {'items' in recommendations && (
            <Recommended
              recommendations={recommendations}
              godtTeasers={godtTeasers}
              isRestaurantSection={isRestaurantReviewArticle}
              isMobile={!!isMobile}
            />
          )}
          <LinkWithPulse
            className="homescreen-button"
            href="/"
            objectId="redirect-to-homescreen"
            name="Redirect to homescreen"
          >
            Gå til Vink-forsiden
          </LinkWithPulse>
          {isRestaurantReviewArticle && (
            <LinkWithPulse
              className="godt-redirect-button"
              href="https://www.godt.no/"
              objectId="redirect-to-godt"
              name="Redirect to Godt"
            >
              Gå til Godt.no
            </LinkWithPulse>
          )}
          <SoMe />
        </div>
      </div>
      <Footer isMobile={isMobile} />
    </div>
  );
};
